<template>
  <div class="rubric-container">
    <div :class="{ 'eg-rubric-card': selectedRubric }">
      <div v-if="selectedRubric" class="eg-rubric-card-container">
        <div class="rubric-description">
          <div class="paragraph-s semi-bold">
            <a
              class="paragraph-s semi-bold"
              href="#"
              @click.stop="openRubricPreview(selectedRubric)"
              >{{ selectedRubric?.name }}</a
            >
          </div>

          <div v-if="hasGradeOrLanguage" class="rubric-tags">
            <div v-if="Boolean(selectedRubric.language)" class="eg-tag">
              {{ selectedRubric.language.label }}
            </div>
          </div>
        </div>

        <div
          v-if="showGradeLevel && Boolean(selectedRubric.gradeLevel)"
          class="d-flex align-items-center justify-content-start"
        >
          <eg-grading-details-level
            :grading-details-level="selectedRubric.gradeLevel"
            size="large"
            is-rubric-card
          />
        </div>
        <div class="rubric-actions-container">
          <div v-if="$scopedSlots.actions">
            <slot name="actions" />
          </div>

          <div v-else class="rubric-actions">
            <div v-for="(rubric, index) in rubricsActions" :key="index">
              <eg-button
                size="small"
                :icon="rubric.icon"
                :type="rubric.iconType || 'secondary'"
                :icon-color="rubric.iconColor"
                @click="rubric.action"
              >
                {{ rubric.label }}
              </eg-button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        v-b-toggle.rubric-library-sidebar
        class="rubric-empty-state-text paragraph-s medium rubric-clickable rubric-empty-state"
      >
        <eg-icon icon="plus-add-more-maximize" />
        {{ $t("rubrics.selectRubricToStart") }}
      </div>
    </div>
  </div>
</template>

<script>
import { openRubricPreview } from "@/components/dashboard/components/open_rubric_preview";
import { formatAuthorValue } from "@/utils/format";
import EgGradingDetailsLevel from "@/components/global/eg/eg-tags/eg_grading_details_level.vue";

export default {
  name: "EgRubricCard",

  components: {
    EgGradingDetailsLevel,
  },

  props: {
    selectedRubric: {
      type: Object,
      default: () => {},
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    isPlatformRubric: {
      type: Boolean,
      default: false,
    },

    showGradeLevel: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {};
  },

  computed: {
    isSharedRubric() {
      return (
        this.$listeners["share-rubric"] &&
        !this.isPlatformRubric &&
        this.selectedRubric?.shareLink
      );
    },

    rubricsActions() {
      const actions = [
        {
          icon: "read-only-view",
          iconType: "ordinary",
          iconColor:
            "var(--color-components-button-primaryOutlined-buttonStroke)",
          action: () => {
            openRubricPreview(this.selectedRubric);
          },
        },
      ];

      if (this.$listeners["select-rubric"]) {
        actions.push({
          icon: "",
          iconType: this.isSelected ? "secondary" : "primary",
          iconColor: "",
          label: this.isSelected
            ? this.$t("rubrics.selected")
            : this.$t("rubrics.select"),
          action: () => {
            this.$emit("select-rubric", this.selectedRubric);
          },
        });
      }

      if (this.$listeners["delete-rubric"] && !this.isPlatformRubric) {
        actions.push({
          icon: "close",
          iconType: "ordinary",
          iconColor: "var(--color-icon-neutral-default)",
          action: () => {
            this.$emit("delete-rubric", this.selectedRubric);
          },
        });
      }

      return actions;
    },

    userDomain() {
      return this.$store.state.user?.domain?.url || "your";
    },

    sharedLabel() {
      if (this.isPlatformRubric) {
        return this.$t("rubrics.anyoneOnEssayGrader");
      }

      if (this.selectedRubric.isOrg) {
        return this.$t("rubrics.organization");
      }

      return this.$t("rubrics.privateLink");
    },

    hasGradeOrLanguage() {
      return Boolean(
        this.selectedRubric.gradeLevel || this.selectedRubric.language
      );
    },
  },

  methods: {
    formatAuthorValue,
    openRubricPreview,
  },
};
</script>

<style scoped lang="scss">
.rubric-container {
  display: flex;
  flex-direction: column;

  .eg-rubric-card-container {
    display: grid;
    align-items: center;
    gap: 1rem;
    grid-template-columns: 1fr 130px 0.2fr;
  }

  .eg-rubric-card {
    gap: 8px;
    padding: 16px;
    background-color: white;
    border-radius: var(--radius-rd-400);
    border: 1px solid var(--color-stroke-neutral-default);
    .eg-grading-details-level {
      flex-shrink: 0;
      background-color: transparent;
      border: 1px solid var(--color-stroke-neutral-default);
    }
  }
  .rubric-empty-state {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 16px;
    text-align: center;
    padding: 34px 24px;
    border: 2px dashed var(--color-stroke-primary-default, #8a3ffc);
    cursor: pointer;
    line-height: 20px;
    .rubric-empty-state-text {
      color: var(--color-text-neutral-secondary);
    }
  }

  .rubric-description {
    flex: 0.9;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rubric-info {
    display: flex;
    flex-direction: row;
    gap: 32px;
    align-items: center;
    justify-content: flex-end;
  }

  .rubric-created-by {
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0;
    color: #6c757d;

    .icon-badge {
      padding: 8px;
      font-size: 12px;
    }
  }

  .rubric-actions-container {
    flex-shrink: 0;
    margin-left: auto;
  }

  .rubric-actions {
    display: flex;
    gap: 12px;
  }

  .rubric-tags {
    display: flex;
    gap: 8px;
  }

  @media (max-width: 576px) {
    .eg-rubric-card-container {
      gap: 8px;
    }
  }
}
</style>
